import { useI18n } from '../../i18n';
import { useGameData } from '../../state/game';
import { BottleCap } from '../app/BottleCap';
import { StatusBadge } from '../app/StatusBadge';
import { computeBracket } from '../../lib/standings';
import { KNOCKOUT_PHASES, type BundleMatch, type Phase, sideLabel } from '../../lib/wcTypes';
import { shortDate } from '../../lib/datetime';

const PHASE_KEY: Record<Phase, string> = {
  group: 'group',
  r32: 'r32',
  r16: 'r16',
  qf: 'qf',
  sf: 'sf',
  third: 'third',
  final: 'final',
};

/** Knockout bracket as vertical phase sections (mobile-friendly, like the app). */
export function BracketView() {
  const { t, locale, localePath } = useI18n();
  const { bundle, pickFor } = useGameData();
  if (!bundle) return null;

  const bracket = computeBracket(bundle.matches);

  const predictedCount = (ms: BundleMatch[]) => ms.filter((m) => pickFor(m.number)).length;

  return (
    <div class="bracket">
      {KNOCKOUT_PHASES.map((phase) => {
        const ms = bracket[phase];
        if (!ms.length) return null;
        const isFinal = phase === 'final';
        return (
          <section key={phase} class="bracket__phase">
            <div class="bracket__head">
              <span class="bc-label">{t(`play.bracket.${PHASE_KEY[phase]}`)}</span>
              <span class="bracket__count">
                {predictedCount(ms)}/{ms.length} {t('play.predicted')}
              </span>
            </div>
            <div class={`bracket__list${isFinal ? ' bracket__list--final' : ''}`}>
              {ms.map((m) => {
                const teamA = m.team_a ? bundle.teams[m.team_a] : undefined;
                const teamB = m.team_b ? bundle.teams[m.team_b] : undefined;
                const pick = pickFor(m.number);
                const hasScore = m.score_a != null && m.score_b != null;
                return (
                  <a key={m.number} class="ko" href={localePath(`/match/${m.number}`)}>
                    <div class="ko__top">
                      <span class="ko__date">{shortDate(m.kickoff_utc, locale)}</span>
                      <StatusBadge status={m.status} minute={m.minute} />
                    </div>
                    <div class="ko__side">
                      <BottleCap flag={teamA?.flag} code={m.team_a} size={26} />
                      <span class="ko__name">{sideLabel(bundle, m.team_a, m.team_a_slot, locale)}</span>
                      <span class="ko__sc">{hasScore ? m.score_a : ''}</span>
                    </div>
                    <div class="ko__side">
                      <BottleCap flag={teamB?.flag} code={m.team_b} size={26} />
                      <span class="ko__name">{sideLabel(bundle, m.team_b, m.team_b_slot, locale)}</span>
                      <span class="ko__sc">{hasScore ? m.score_b : ''}</span>
                    </div>
                    {pick && (
                      <div class="ko__pick">
                        {t('game.your_pick')} {pick.scoreA}–{pick.scoreB}
                      </div>
                    )}
                  </a>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
